import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';

import {
  asText,
  getProductsFromOpenSearchResponse,
  getSlicePadding,
  SORT_BY_MAP,
} from '@/utils';
import {
  Container,
  Heading,
  Product,
  ProductPlaceholder,
  useData,
} from '@/components';
import { useEcomStoreSelector } from '@/data';
import { useSelectedStore, useShowByLocationSlug } from '@/hooks';
import { IProductListSlice, SortBySelectOption } from '@/types';
import {
  buildSearchBodyFromParams,
  parseInventoryLocationIds,
  SearchService,
} from '@/opensearch';
import { useFulFillmentType } from '@/hooks/useFulFillmentType';

import styles from './productslist.module.scss';

const ProductsList: FC<IProductListSlice> = slice => {
  const { store } = useSelectedStore();
  const { fulfillmentType } = useFulFillmentType();
  const { showContent } = useShowByLocationSlug(
    slice?.primary?.location_slug as string,
  );
  const { productCard } = useData();
  const { spacingClass, isLargeCard, isProductCardLarge } =
    productCard.displayProps;
  const initProducts = slice?.products ?? [];
  const selectedStore = slice?.selectedStore;
  const { provider_inventory_location_id } = useEcomStoreSelector([
    'provider_inventory_location_id',
  ]);
  const inventoryLocationIds = parseInventoryLocationIds(
    provider_inventory_location_id,
  );
  const isFilterByStore = slice?.primary?.filter_by_store;

  const paddingSection = slice?.primary?.padding_section;
  const marginBottom = slice?.primary?.bottom_spacing;
  const backgroundColor = slice?.primary?.background_color;
  const backgroundImage = slice?.primary?.background_image?.url;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;
  const headerColor = slice?.primary?.header_color;

  const ids = slice?.items
    ?.map(item => item.product_id?.trim())
    .filter((id): id is string => !!id);

  const queryKey = [
    'searchProducts',
    store?.short_name,
    provider_inventory_location_id,
    fulfillmentType,
    ...ids,
  ];
  const { search } = new SearchService({
    entityId: selectedStore?.mso_store_entity_id,
    orgId: selectedStore?.mso_org_id,
    isStrictMedicalEnabled: selectedStore?.strict_medical_enabled,
    inventoryLocationIds,
    fulfillmentType,
  });

  const { data, isLoading } = useQuery({
    enabled: isFilterByStore && ids.length > 0,
    initialData: {
      products: initProducts,
    },
    queryKey,
    queryFn: async () => {
      const idParam = { id: ids };

      const sortBy = SORT_BY_MAP?.[
        slice?.primary?.sort_by
      ] as SortBySelectOption;

      const sortParam = {
        sort:
          sortBy && sortBy.value !== 'default'
            ? `${sortBy.value.field}${capitalize(sortBy.value.order)}`
            : '',
      };

      const { searchBody } = buildSearchBodyFromParams({
        searchParams: {
          ...sortParam,
          ...idParam,
        },
        omitAggs: true,
      });

      const result = await search({ searchBody });

      const products = getProductsFromOpenSearchResponse({
        result,
        isSortingApplied: !!sortBy,
      });

      return { products };
    },
  });

  const productsCollection = data?.products;

  if (!slice?.primary?.active || !productsCollection.length || !showContent) {
    return null;
  }

  return (
    <section
      className={classNames(
        styles.prodcut__list,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: marginBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      key={store?.short_name}
      style={{
        background: backgroundColor!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Container className={styles.container}>
        <Heading level={2} style={{ color: headerColor! }}>
          {asText(slice?.primary?.products_header) ?? 'Products'}
        </Heading>
        <div
          className={classNames(styles.content, spacingClass, {
            [styles.content__large]: isLargeCard,
            [styles.content__large_variation]: isProductCardLarge,
          })}
        >
          {isLoading
            ? new Array(20)
                .fill(null)
                .map((_, index) => (
                  <ProductPlaceholder key={index} largeCard={isLargeCard} />
                ))
            : productsCollection?.map((prod, index) => (
                <Product
                  className={styles.content__product}
                  key={index}
                  product={prod}
                />
              ))}
        </div>
      </Container>
    </section>
  );
};

export default ProductsList;
